import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useForm, Controller } from "react-hook-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../context/AlertContext";
import InfiniteDeviceSelect, { useReportDeviceIds } from "../../../components/InfiniteDeviceSelect";
import { allDevicesQueryOptions } from "../../../features/devices/queries";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import { useReportVoltageStore } from "../../../store/reports/reportVoltageStore";
import ReportPointsTable, { type ReportTableColumn } from "../components/ReportPointsTable";
import { ReportStatCard, ReportChart, tickColor, gridColor, reportLegend } from "../components/ReportCharts";
import ReportLoadingPanel from "../components/ReportLoadingPanel";
import { toLocalInputValue, makeDateTimeFormatter, resolveTz } from "../utils/reportDateFormat";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import { lowVolts } from "../utils/voltage";
import { fmtDuration } from "../../quick-reports/quickReports.compute";
import type { ChartConfiguration } from "chart.js";
import ReportActionButton from "../components/ReportActionButton";
import ReportDateRangePicker from "../components/ReportDateRangePicker";

// Como «Ver temperatura»: se lee el historial en el navegador, así que el
// rango tiene el mismo tope.
const MAX_RANGE_DAYS = 31;
const MAX_CHART_POINTS = 300;
const DAY_MS = 24 * 60 * 60 * 1000;

interface VoltageFormData {
  devices: string[];
  startDate: string;
  endDate: string;
}

interface EpisodeRow {
  id: string;
  unidad: string;
  tipo: string;
  inicio: string;
  fin: string;
  duracion: string;
  minimo: string;
  ubicacion: string;
}

const COLUMNS: ReportTableColumn<EpisodeRow>[] = [
  { key: "unidad", header: "Unidad" },
  { key: "tipo", header: "Tipo" },
  { key: "inicio", header: "Inicio" },
  { key: "fin", header: "Fin" },
  { key: "duracion", header: "Duración", align: "right" },
  { key: "minimo", header: "Voltaje mínimo", align: "right" },
  { key: "ubicacion", header: "Ubicación" },
];

const downsample = <T,>(arr: T[], max: number): T[] => {
  if (arr.length <= max) return arr;
  const step = Math.ceil(arr.length / max);
  return arr.filter((_, i) => i % step === 0);
};

const fmtV = (n: number | null) => (n == null ? "N/A" : `${n.toFixed(1)} V`);

export default function VoltageSection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const { loadedDeviceIds, loadedCount, totalDevices } = useReportDeviceIds();
  const { showAlert } = useAlert();

  const loadRange = useReportVoltageStore((s) => s.loadRange);
  const clear = useReportVoltageStore((s) => s.clear);
  const resultsState = useReportVoltageStore((s) => s.results);
  const order = useReportVoltageStore((s) => s.order);
  const loading = useReportVoltageStore((s) => s.loading);
  const tz = useReportVoltageStore((s) => s.tz);

  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [loadAttempted, setLoadAttempted] = useState(false);

  const { control, setValue, watch, getValues, register, formState: { errors } } = useForm<VoltageFormData>({
    mode: "onBlur",
    defaultValues: { devices: [], startDate: "", endDate: "" },
  });

  const { companyId, disableWithoutCompany } = useReportCompanyScope(setValue, () => {
    clear();
    setLoadAttempted(false);
  });
  const { data: allDevices } = useQuery({ ...allDevicesQueryOptions(companyId) });

  const selectedDevices = watch("devices");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const rangeDays = startDate && endDate ? (new Date(endDate).getTime() - new Date(startDate).getTime()) / DAY_MS : -1;
  const ready = (selectedDevices?.length ?? 0) > 0 && rangeDays >= 0 && rangeDays <= MAX_RANGE_DAYS;

  const handleSelectAll = () => {
    setValue("devices", selectedDevices?.length === loadedDeviceIds.length ? [] : loadedDeviceIds);
  };

  const nameById = useCallback((): Record<string, string> => {
    const map: Record<string, string> = {};
    (allDevices ?? []).forEach((d: any) => {
      map[d.id] = d.vehicle?.name || d.identifier || d.id;
    });
    return map;
  }, [allDevices]);

  const onView = async () => {
    const { devices, startDate: from, endDate: to } = getValues();
    if (rangeDays > MAX_RANGE_DAYS) {
      showAlert({
        title: "Rango demasiado amplio",
        text: `Ver voltaje muestra hasta ${MAX_RANGE_DAYS} días.`,
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }
    const names = nameById();
    await loadRange(
      devices.map((id) => ({ deviceId: id, vehicleName: names[id] || id })),
      new Date(from).toISOString(),
      new Date(to).toISOString(),
    );
    setLoadAttempted(true);
  };

  const results = useMemo(() => order.map((id) => resultsState[id]).filter(Boolean), [order, resultsState]);
  const fmtDateTime = useMemo(() => makeDateTimeFormatter(resolveTz(tz), false), [tz]);

  const totals = useMemo(() => {
    let min: number | null = null;
    let sum = 0;
    let count = 0;
    let cuts = 0;
    let cutMs = 0;
    let lows = 0;
    results.forEach((r) => {
      r.samples.forEach((s) => {
        if (s.supply == null) return;
        sum += s.supply;
        count++;
        min = min == null ? s.supply : Math.min(min, s.supply);
      });
      r.episodes.forEach((e) => {
        if (e.kind === "cut") {
          cuts++;
          cutMs += e.end - e.start;
        } else lows++;
      });
    });
    return { min, avg: count ? sum / count : null, cuts, cutMs, lows, readings: results.reduce((n, r) => n + r.samples.length, 0) };
  }, [results]);

  const rows = useMemo<EpisodeRow[]>(
    () =>
      results.flatMap((r) =>
        r.episodes.map((e, i) => ({
          id: `${r.deviceId}-${e.start}-${i}`,
          unidad: r.vehicleName,
          tipo: e.kind === "cut" ? "Sin energía" : `Voltaje bajo (< ${lowVolts(r.system)} V)`,
          inicio: fmtDateTime(e.start),
          fin: fmtDateTime(e.end),
          duracion: fmtDuration((e.end - e.start) / 1000),
          minimo: fmtV(e.minVolts),
          ubicacion: e.lat != null && e.lng != null ? `${e.lat.toFixed(5)}, ${e.lng.toFixed(5)}` : "—",
        })),
      ),
    [results, fmtDateTime],
  );

  const chartConfig = useMemo<ChartConfiguration | null>(() => {
    const withData = results.filter((r) => r.samples.some((s) => s.supply != null));
    if (withData.length === 0) return null;
    let minTs = Infinity;
    let maxTs = -Infinity;
    withData.forEach((r) =>
      r.samples.forEach((s) => {
        minTs = Math.min(minTs, s.ts);
        maxTs = Math.max(maxTs, s.ts);
      }),
    );
    const axis = new Intl.DateTimeFormat("es-MX", {
      timeZone: resolveTz(tz),
      day: "2-digit",
      month: "2-digit",
      ...(maxTs - minTs > 3 * DAY_MS ? {} : { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }),
    });
    const datasets: any[] = withData.map((r) => ({
      label: r.vehicleName,
      data: downsample(
        r.samples.filter((s) => s.supply != null),
        MAX_CHART_POINTS,
      ).map((s) => ({ x: s.ts, y: Number(s.supply!.toFixed(2)) })),
      borderColor: r.color,
      backgroundColor: r.color,
      borderWidth: 2,
      pointRadius: 0,
      tension: 0.2,
    }));
    // Una línea de «bajo» por cada sistema presente (12 y/o 24 V).
    [...new Set(withData.map((r) => r.system))].forEach((system) => {
      const y = lowVolts(system);
      datasets.push({
        label: `Bajo en ${system} V (${y} V)`,
        data: [
          { x: minTs, y },
          { x: maxTs, y },
        ],
        borderColor: "#DC2626",
        borderWidth: 1.5,
        borderDash: [6, 4],
        pointRadius: 0,
      });
    });
    return {
      type: "line",
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "nearest", intersect: false },
        plugins: {
          legend: reportLegend(isDarkMode),
          tooltip: {
            callbacks: {
              title: (items: any[]) => fmtDateTime(Number(items[0].parsed.x)),
              label: (item: any) => `${item.dataset.label}: ${item.parsed.y} V`,
            },
          },
        },
        scales: {
          x: {
            type: "linear",
            ticks: {
              color: tickColor(isDarkMode),
              callback: (value) => axis.format(new Date(Number(value))),
              maxTicksLimit: 10,
              maxRotation: 0,
              autoSkip: true,
            },
            grid: { color: gridColor(isDarkMode) },
          },
          y: {
            beginAtZero: true,
            title: { display: true, text: "V", color: tickColor(isDarkMode) },
            ticks: { color: tickColor(isDarkMode) },
            grid: { color: gridColor(isDarkMode) },
          },
        },
      },
    };
  }, [results, isDarkMode, tz, fmtDateTime]);

  const onDownload = async (type: "pdf" | "excel") => {
    const { startDate: from, endDate: to } = getValues();
    const fileName = `voltaje_${from.split("T")[0]}_${to.split("T")[0]}.${type === "pdf" ? "pdf" : "xlsx"}`;
    const period =
      `Período: ${from.replace("T", " ")} al ${to.replace("T", " ")}` +
      ` | Cortes: ${totals.cuts} (${fmtDuration(totals.cutMs / 1000)}) | Voltaje bajo: ${totals.lows}`;
    setExporting(type);
    await new Promise((r) => setTimeout(r, 50));
    try {
      const meta = { title: "Informe de Voltaje y energía", period, fileName };
      if (type === "excel") await exportHistoricToExcel(rows, COLUMNS, meta);
      else exportHistoricToPdf(rows, COLUMNS, meta);
    } catch {
      showAlert({
        title: "Error al descargar",
        text: "No se pudo generar el archivo de voltaje.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    const now = new Date();
    setValue("startDate", toLocalInputValue(new Date(now.getTime() - 7 * DAY_MS)));
    setValue("endDate", toLocalInputValue(now));
  }, [setValue]);

  useEffect(() => () => clear(), [clear]);

  const hasResults = totals.readings > 0;
  const withoutData = results.filter((r) => !r.loading && r.samples.length === 0);

  const cards = [
    { label: "Voltaje promedio", value: fmtV(totals.avg), icon: "ri-flashlight-line" },
    { label: "Voltaje mínimo", value: fmtV(totals.min), icon: "ri-battery-low-line" },
    { label: "Cortes de energía", value: `${totals.cuts}${totals.cuts ? ` · ${fmtDuration(totals.cutMs / 1000)}` : ""}`, icon: "ri-plug-line" },
    { label: "Voltaje bajo", value: String(totals.lows), icon: "ri-alarm-warning-line" },
  ];

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4">
      <div className={`flex flex-col lg:flex-row ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-3.5 gap-3 justify-between lg:items-end`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex flex-col gap-1 min-w-52 w-full lg:w-auto">
            <div className="flex justify-between items-center">
              <label htmlFor="devices" className={`${themeClasses.text} text-xs`}>
                Equipos {selectedDevices?.length > 0 && `(${selectedDevices.length}/${totalDevices})`}
              </label>
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={disableWithoutCompany}
                className={`text-xs px-2 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} hover:opacity-90 disabled:opacity-50`}
              >
                {selectedDevices?.length === loadedDeviceIds.length && loadedCount > 0 ? "Deseleccionar todos" : `Seleccionar todos (${loadedCount})`}
              </button>
            </div>
            <Controller
              name="devices"
              control={control}
              render={({ field }) => (
                <InfiniteDeviceSelect value={field.value} onChange={field.onChange} isDarkMode={isDarkMode} themeClasses={themeClasses} />
              )}
            />
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Rango de fechas</label>
            <input type="hidden" {...register("startDate", { required: "Fecha inicio requerida" })} />
            <input type="hidden" {...register("endDate", { required: "Fecha fin requerida" })} />
            <ReportDateRangePicker
              startValue={startDate}
              endValue={endDate}
              onChangeStart={(v) => setValue("startDate", v, { shouldValidate: true, shouldDirty: true })}
              onChangeEnd={(v) => setValue("endDate", v, { shouldValidate: true, shouldDirty: true })}
              startError={errors.startDate?.message}
              endError={errors.endDate?.message}
            />
          </div>
        </div>
        <ReportActionButton
          type="button"
          onClick={onView}
          disabled={loading || !ready || disableWithoutCompany}
          title={rangeDays > MAX_RANGE_DAYS ? `Ver voltaje admite hasta ${MAX_RANGE_DAYS} días` : undefined}
        >
          Ver voltaje
        </ReportActionButton>
      </div>

      {loading && (
        <ReportLoadingPanel
          isDarkMode={isDarkMode}
          icon="ri-flashlight-line"
          title="Obteniendo voltaje…"
          subtitle="Leyendo la alimentación y la batería del equipo en el rango"
        />
      )}

      {hasResults && !loading && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className={`text-sm ${themeClasses.text}`}>
              {results.length} unidad(es) · {totals.readings} lectura(s)
              {withoutData.length > 0 && ` · ${withoutData.length} sin voltaje`}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {(["pdf", "excel"] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => onDownload(type)}
                  disabled={exporting !== null || rows.length === 0}
                  title={rows.length === 0 ? "Sin cortes ni voltaje bajo que descargar" : undefined}
                  className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
                >
                  {exporting === type ? "Generando..." : `Descargar ${type === "pdf" ? "PDF" : "Excel"}`}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  clear();
                  setLoadAttempted(false);
                }}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2}`}
              >
                Cerrar
              </button>
            </div>
          </div>

          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            {cards.map((c) => (
              <ReportStatCard key={c.label} icon={c.icon} label={c.label} value={c.value} isDarkMode={isDarkMode} />
            ))}
          </div>

          {chartConfig && (
            <div className={`p-3 rounded-xl border shadow-sm ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-50 border-gray-200"}`}>
              <p className={`text-sm font-semibold mb-2 ${themeClasses.text}`}>Voltaje de alimentación en el tiempo</p>
              <ReportChart config={chartConfig} height={300} />
            </div>
          )}

          <ReportPointsTable
            data={rows}
            columns={COLUMNS}
            isDarkMode={isDarkMode}
            emptyText="Sin cortes de energía ni voltaje bajo en el rango."
          />
        </div>
      )}

      {!hasResults && !loading && (
        <div className={`mt-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className={`flex flex-col items-center justify-center gap-3 px-6 py-10 text-center rounded-xl border border-dashed ${themeClasses.cardBorder} ${isDarkMode ? "bg-white/5" : "bg-black/[0.02]"}`}>
            <span className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${isDarkMode ? "bg-primary-500/15" : "bg-primary-500/10"}`}>
              <i className="ri-flashlight-line text-primary-500 text-2xl" />
            </span>
            <p className={`text-sm font-semibold ${themeClasses.text}`}>
              {loadAttempted ? "Sin voltaje en el rango" : "Voltaje y energía"}
            </p>
            <p className={`text-xs max-w-md ${themeClasses.textSecondary}`}>
              {loadAttempted
                ? "Los equipos seleccionados no reportan voltaje de alimentación ni de batería en esas fechas."
                : `Elige equipos y fechas (hasta ${MAX_RANGE_DAYS} días) para ver el voltaje de alimentación, los cortes de energía y los tramos con voltaje bajo.`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
